import { yupResolver } from '@hookform/resolvers/yup'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { selectAddress } from 'feature/addresses/redux/addressCreateSelector'

import { doRegistration } from 'feature/auth/redux/action/doRegistration'
import { AuthStatus, SmsStatus } from 'feature/auth/redux/authSlice'
import { setAuthRefreshAccessToken } from 'feature/auth/redux/authStorage'
import { useSendForm } from 'feature/productOrder/hooks/useSendForm'
import { RegistrationData, TSuggest } from 'feature/registration/types'
import { messages } from 'helpers/validationMessages'
import { MainLayout } from 'layouts'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { Loading } from 'router/Loading'
import { useActions, useAppSelector, useThunkDispatch } from 'store/hooks'
import { CreateModalConfirm } from 'widget/CreateModalConfirm'
import { UserDataForm } from 'widget/UserDataForm/UserDataForm'
import * as yup from 'yup'

import {
  deleteRegistrationFields,
  getRegistrationFields,
  setRegistrationFields,
} from 'feature/registration/storage/registrationStorage'
import { NavigationPrompt } from 'widget/NavigationPrompt/NavigationPrompt'
import { FIAS_LEVEL } from 'constants/const'

const schema = yup.object().shape({
  firstname: yup
    .string()
    .required(messages.required)
    .max(100, messages.maxLength(100))
    .test('notOnlySpaces', messages.required, value => value.trim() !== ''),
  lastname: yup
    .string()
    .required(messages.required)
    .max(100, messages.maxLength(100))
    .test('notOnlySpaces', messages.required, value => value.trim() !== ''),
  middlename: yup.string().max(100, messages.maxLength(100)),
  personalAgree: yup
    .boolean()
    .test('personal-agree', '', value => value === true),
  advAgree: yup.boolean(),
})

export function Registration() {
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isExitModal, setIsExitModal] = useState(true)
  const [isLiving, setIsLiving] = useState(false)

  const {
    clearMap,
    clearAddress,
    setInputAddress,
    setInputAddressSuggest,
    setIsValidAddress,
    setClickMarkCoords,
  } = useActions()
  const { inputSuggest, input } = useAppSelector(selectAddress)
  const [orderLoading, setOrderLoading] = useState<boolean>(false)

  const formMethods = useForm<RegistrationData>({
    defaultValues: {
      firstname: '',
      lastname: '',
      middlename: '',
      address: {
        address: '',
        fiasId: '',
        fiasLevel: '',
      },
      personalAgree: false,
      advAgree: false,
    },
    // @ts-ignore
    resolver: yupResolver(schema),
    mode: 'all',
  })
  const thunkDispatch = useThunkDispatch()

  const { authInfo } = useAppSelector(state => state.auth)
  const { setUserInfo, setAuthInfo, setSmsInfo } = useActions()

  const location = useLocation()
  const { sendForm } = useSendForm()

  const { watch } = formMethods

  const watchFields = watch()

  useEffect(() => {
    if (authInfo.status !== AuthStatus.smsConfirmed) {
      clearMap()
      clearAddress()
      deleteRegistrationFields()
    }
    switch (authInfo.status) {
      case AuthStatus.guest:
        navigate('/login')
        break
      case AuthStatus.smsConfirmedCompany:
        navigate('/company/registration')
        break
      case AuthStatus.registered:
        if (location?.state?.sendingForm) {
          setOrderLoading(true)
          sendForm({ forceNavigate: false })
          return
        }
        navigate('/')
        break
    }
  }, [authInfo.status])

  useEffect(() => {
    if (!isSubmitting && !isLiving) {
      if (inputSuggest) {
        setRegistrationFields({
          ...watchFields,
          address: inputSuggest,
        })
      } else setRegistrationFields(watchFields)
    }
  }, [watchFields, isSubmitting])

  useEffect(() => {
    if (!isSubmitting && !isLiving) {
      setRegistrationFields({
        ...watchFields,
        address:
          (inputSuggest && inputSuggest.address) === input
            ? inputSuggest
            : null,
      })
    }
  }, [watchFields, isSubmitting])

  useEffect(() => {
    const field = getRegistrationFields()

    formMethods.reset(field)

    if (!field.address) return
    if (field.address?.address.length !== 0) {
      setInputAddressSuggest(field.address)
      setIsValidAddress(Number(field.address.fiasLevel) >= FIAS_LEVEL)
      setInputAddress(field.address?.address)
      setClickMarkCoords([
        Number(field.address.geoLat),
        Number(field.address.geoLon),
      ])
    }
  }, [])

  const onSubmit: () => void = async () => {
    setIsExitModal(false)
    const currentValues = formMethods.getValues()

    currentValues.address = inputSuggest as TSuggest

    setIsSubmitting(true)

    thunkDispatch(doRegistration(currentValues))
  }

  const onConfirm = async () => {
    setIsLiving(true)
    deleteRegistrationFields()
    clearMap()
    clearAddress()
    setAuthRefreshAccessToken(null)
    setAuthInfo({
      status: AuthStatus.guest,
      accessToken: null,
      refreshToken: null,
    })
    setSmsInfo({ status: SmsStatus.readyForSend })
    setUserInfo({ phone: '' })
  }

  if (orderLoading)
    return (
      <MainLayout>
        <Loading />
      </MainLayout>
    )

  return (
    <MainLayout>
      <FormProvider {...formMethods}>
        <div className='container'>
          <UserDataForm
            title='Личные данные'
            sendButton={{
              endIcon: <ChevronRightIcon />,
              text: 'Завершить',
            }}
            onSubmit={onSubmit}
          />
        </div>
      </FormProvider>
      <NavigationPrompt when={isExitModal} onConfirm={onConfirm}>
        {CreateModalConfirm()}
      </NavigationPrompt>
    </MainLayout>
  )
}
